/**
 * What each authority rung DOES, in words — one copy for the Authority screen and for Ask (MAR-300).
 *
 * Ask used to describe the rungs from its own idea of them, and the idea was wrong in exactly the
 * way that matters: it told a Shadow merchant that nothing would go out "unless you confirm it",
 * and an Assisted one that she would wait for a "one-tap confirm". Neither rung asks for anything.
 * The model now reads these statements through `read_authority` and is told never to paraphrase
 * them, and the web renders the same module, so the screen and the answer cannot drift apart.
 *
 * Every sentence here is a claim about the code, and each names what it rests on:
 *
 *  - **Shadow starts nothing, and holds her replies too.** The gateway's send paths check
 *    `sendHoldOf` (via `shouldShadow`) and file the would-be decision for review instead of sending
 *    (`shadow-interceptor.js`), so nothing Marva would START goes out. Since MAR-241 every
 *    shopper-facing REPLY site asks the same predicate, `@marva/shared/send-hold`'s `sendHoldOf`,
 *    before it sends — re-verified 2026-10-25:
 *     - EMAIL replies: `email-reply-handler` holds after the inbound turn is on the thread and
 *       before sender resolution and the model call (`sendHoldOf(workspace, 'email')`), recorded
 *       `withheld_shadow` / `withheld_disabled`.
 *     - Templated ON-SITE CHAT frames: `websocket-chat`'s `sendAndRecordReply` holds every recorded
 *       templated reply, and the greeting, `suitability_pending` and pending replay ask
 *       `chatSendHold` themselves (`sendHoldOf(workspace, 'chat')`); the model's chat answers go
 *       through the gateway, which holds the same way.
 *     - SMS replies and the turn-cap handoff run after `sendHoldOf(workspace, 'sms')`
 *       (`sms-reply-handler`).
 *    Exempt, deliberately, and so never claimed as held: the STOP/HELP compliance texts
 *    (`sms-webhook-handler` — a carrier obligation, not Marva speaking; Shadow copy names them),
 *    merchant-facing mail, and a merchant's own test sends (to the merchant, not a customer).
 *    Replies sent before MAR-241 went out in Shadow; the Ledger keeps saying so on those rows
 *    (`reply-vocabulary.js`, `SHADOW_IGNORED_CHIP`). Review feedback (`submitFeedback`) writes
 *    `feedbackStatus` and nothing else — it approves nothing and sends nothing.
 *  - **The rung never asks.** A rung refusal (`ABOVE_RUNG`, `CLASS_OFF`, `CONFIG_UNAVAILABLE`) is
 *    silent: it emails nobody and queues nothing for approval (`decision-vocabulary.js`,
 *    `AUTHORITY_REASONS`). Inbox escalations come from separate holds, not from the rung.
 *  - **Autonomous grants nothing.** Every routed class needs `assisted` (`HIGHEST_EFFECTIVE_RUNG`,
 *    `action-classes.js`), and `prepare_authority_change`'s schema refuses `autonomous`.
 *  - **Above the ceiling** depends on `AUTHORITY_ESCALATION_ENABLED` — see `aboveCeilingFor`.
 *  - **The mode can hold what the rung allows.** Trial expiry writes `mode: 'shadow'` and leaves an
 *    existing authority row alone, and `shouldShadow` then holds every gateway send whatever the
 *    rung says — see `heldByModeFor`.
 *
 * ⚠️ Dependency-free, like `./action-classes`: the web app bundles it for a browser. No `process`,
 * no AWS, no requires. Whoever knows the flag passes it in.
 *
 * @module authority-copy
 */

'use strict';

/**
 * The Authority screen's rung copy — moved here from the web app's `rungCopy.ts`, which
 * re-exports it. Shown to merchants; `name` is also what Ask's proposal card calls a rung.
 *
 * Shadow's line names what it holds — what she starts AND her replies — and the one exemption a
 * customer can still receive, the STOP/HELP texts carriers require (see the module docblock). It
 * said email replies and templated on-site messages still went out until MAR-241 held them.
 */
const RUNG_COPY = Object.freeze({
  shadow: Object.freeze({
    name: 'Shadow',
    tag: '01',
    desc: 'She reads everything and drafts everything. Nothing she starts leaves the building, and her replies to customers who write in are held too.'
  }),
  assisted: Object.freeze({
    name: 'Assisted',
    tag: '02',
    desc: 'She acts inside your limits, and stops at anything you have not allowed. Where everyone starts.'
  }),
  autonomous: Object.freeze({
    name: 'Autonomous',
    tag: '03',
    desc: 'Returns, exchanges and carts. Nothing asks her to do these yet, so this rung would change nothing.'
  })
});

/**
 * The Authority screen's "What <rung> means" line — moved from `AuthorityPanel.tsx`. Shadow's
 * names the STOP/HELP exemption rather than claim that nothing at all goes out.
 * `shadow` for the Shadow rung, `acting` for every rung that acts.
 */
const RUNG_MEANING_COPY = Object.freeze({
  shadow: 'Nothing she starts goes out, and her replies to customers who write in are held too. She drafts them, and you read what she would have done. The automatic STOP and HELP texts carriers require still send.',
  acting: 'These are the things the rung permits. Something she has not been allowed to do she simply does not do — she will not ask you to approve it.'
});

const SHADOW_CORE = 'Shadow: she starts nothing on her own. No email or SMS she would initiate (follow-ups, cart reminders, offers) is sent; ' +
  'she decides and records what she would have done. ' +
  'There is nothing for the merchant to confirm on any message, and nothing she drafts waits for approval. ' +
  'Her Shadow decisions are there to review and give feedback on, not to approve: feedback sends nothing. ';

/**
 * The model's Shadow statement (see the module docblock for the reply paths): what she starts and
 * her replies on every channel are held; the STOP/HELP compliance texts are the stated exemption.
 * @returns {string}
 */
function shadowStatementFor() {
  return `${SHADOW_CORE}Her replies are held too: email, SMS (including the turn-cap handoff) and on-site chat replies to a customer who writes in are not sent in Shadow. ` +
    // "are still sent", not "are exempt": Ask quotes this verbatim, and "exempt" never says the texts
    // go out, so a verbatim answer left the merchant to infer that they do (MAR-461).
    'The automatic STOP and HELP texts carriers require are still sent: they are compliance messages, not Marva speaking.';
}

/**
 * What each rung means, stated for the model. Ask describes a rung ONLY from these — never in
 * its own words — so each is complete enough to answer "does she ask me first?" on its own.
 */
const RUNG_FOR_MODEL = Object.freeze({
  // The workspace-independent statement; `readAuthority` returns `shadowStatementFor` the
  // workspace's own state instead.
  shadow: shadowStatementFor(),
  assisted: 'Assisted: she acts on her own inside the merchant\'s limits and exceptions. ' +
    'Anything the rung or an exception does not allow she simply does not do; she does not ask the merchant to approve it. ' +
    'The rung itself never asks for a confirmation: escalations in the Inbox come from separate holds, not from the rung.',
  autonomous: 'Autonomous: grants nothing beyond Assisted today. ' +
    'Nothing asks her to start returns, exchanges or carts yet, so a workspace on Autonomous can do exactly what an Assisted one can. ' +
    'It cannot be chosen.'
});

/**
 * What Marva does with an offer above the merchant's discount ceiling while the hold is dark, in the
 * merchant's voice (MAR-314; moved here from the web's retired `ceilingCopy.ts`). The web no longer
 * renders it directly: governance-api's authority GET sends `aboveCeilingForMerchant(flag)`, which
 * is this sentence while the flag is dark.
 *
 * ⚠️ TODAY's behaviour, while `AUTHORITY_ESCALATION_ENABLED` is off: above the ceiling the offer
 * is brought down to the ceiling and sent (`executor-router.js`, the escalate branch falls through
 * to the send). The ceiling never holds it and puts nothing in the Inbox — discount-governance's own
 * escalation thresholds are a separate hold (`DISCOUNT_ESCALATION_NOTE`).
 * MAR-107 (the above-ceiling merchant hold) changes this; the screen (`aboveCeilingForMerchant`,
 * via the GET) and Ask (`aboveCeilingFor`) both already follow the flag.
 */
const ABOVE_CEILING_COPY = 'Above your ceiling she offers the ceiling instead.';

/**
 * The OTHER thing that can hold a discount, and not the ceiling: `discount-governance`'s own
 * escalation (`checkEscalation`). It fires when the percent exceeds `escalation.thresholdPercent`
 * or the cart value exceeds `escalation.thresholdCartValue` — both workspace settings, defaulted in
 * code when unset, so it is ON for every workspace unless configured otherwise — and its
 * `defaultAction` (`pause-notify` unless set) holds the offer behind an Inbox escalation
 * (`emitEscalation`); `act-and-notify` sends and only notifies. Numbers are never stated here:
 * they are per-workspace.
 */
const DISCOUNT_ESCALATION_NOTE = 'Separately, the store\'s discount escalation thresholds (a discount percent or a cart value above its escalation settings) ' +
  'can still hold an offer in the Inbox for the merchant to approve or reject, unless the store has set them to act and notify instead.';

/**
 * What happens above the ceiling, for the model, given the live state of
 * `AUTHORITY_ESCALATION_ENABLED` (read by the caller — this module never touches `process`).
 *
 * Enabled, the gateway's escalate branch parks the outbound decision (`parkForApproval`) and files a
 * `discount_percent` escalation. Approval re-dispatches it at the proposed percent
 * (`scheduled-executor.js`, `approvedStretch`); rejection or expiry cancels it
 * (`finalizeHeldDecision`). Since MAR-157 both outbound, pausable channels pass through
 * `governOutboundSend`, so the hold applies to email and SMS; real-time on-site channels clamp.
 *
 * @param {boolean} escalationEnabled
 * @returns {string}
 */
function aboveCeilingFor(escalationEnabled) {
  const ceiling = escalationEnabled === true
    ? 'Above the merchant\'s discount ceiling, on email or SMS, she holds the message and asks in the Inbox: ' +
      'approved, it sends at the larger discount she proposed; rejected or left to expire, it is not sent. ' +
      'Only email and SMS are held this way; on other channels she offers the ceiling instead.'
    : `${ABOVE_CEILING_COPY} The ceiling itself never makes anything wait for the merchant's approval.`;
  return `${ceiling} ${DISCOUNT_ESCALATION_NOTE}`;
}

/**
 * The note for a workspace whose MODE holds sends its rung would allow — trial expiry demotes the
 * mode to `shadow` and does not touch an existing authority row (`trial-manager`,
 * `setWorkspaceMode`). The shared send-hold predicate also holds every send while disabled.
 *
 * A rowless workspace has no stored rung to name (MAR-444): pass `null` and the disabled note
 * names none, rather than calling the `rungFromMode` stand-in "the authority rung".
 *
 * @param {string|null} rungName - the stored rung's display name; null when no row is saved
 * @param {string} [mode='shadow'] - the workspace mode imposing the hold
 * @returns {string}
 */
function heldByModeFor(rungName, mode = 'shadow') {
  if (mode === 'disabled') {
    if (rungName === null) {
      return 'The workspace itself is disabled, so every customer send is held. Until it is re-enabled she cannot act.';
    }
    return `The authority rung is ${rungName}, but the workspace itself is disabled, so every customer send is held. ` +
      'Until the workspace is re-enabled the rung cannot act.';
  }
  return `The authority rung is ${rungName}, but the workspace itself is in Shadow mode (for example because a trial ended), ` +
    'and that mode holds everything she would start and her replies too. Until the mode changes she behaves as in Shadow.';
}

/**
 * The note for a workspace with NO authority row. The governance GET still projects
 * `configuredRung: 'assisted'` there as the safe activation target (MAR-26), but Ask narrated
 * that as a rung the merchant had configured (MAR-444), so Ask's read states the absence instead.
 * Worded per mode, because "her default is Assisted" reads as "she is on Assisted" in Shadow and
 * claims something acts while disabled.
 *
 * @param {string|null} mode - the workspace mode
 * @returns {string}
 */
function noSavedAuthorityFor(mode) {
  const none = 'No authority has been saved for this store yet, so there is no configured rung.';
  if (mode === 'disabled') return `${none} The workspace is disabled, so nothing acts.`;
  if (mode === 'shadow') {
    return `${none} If the store goes live with nothing saved, she will act as Assisted; ` +
      'that is a default, not a rung the merchant chose.';
  }
  return `${none} She acts as Assisted because that is the default, not a rung the merchant chose.`;
}

/**
 * The channels a send path asks `shouldShadow` about by name — the `shadowedChannels` keys
 * (`WorkspaceSchema.shadowedChannels`, which reads this list), as passed by the gateway
 * (`executor-router.js`, `shouldShadow(workspace, channel)`) and `email-generator`
 * (`shouldShadow(workspace, 'email')`). The gateway's other channels (popup, reaction_prompt,
 * contact_capture) can only be held by `mode`. With their display names.
 */
const SHADOWABLE_CHANNELS = Object.freeze(['email', 'sms', 'chat']);
const CHANNEL_NAMES = Object.freeze({ email: 'Email', sms: 'SMS', chat: 'On-site chat' });

/**
 * The note for a workspace that acts, but has some channels held in Shadow by
 * `shadowedChannels` (overage exhaustion, deliverability — `shadowChannel`). It is deliberately
 * not scoped to messages Marva starts: every reply site asks `sendHoldOf(workspace, channel)`, which
 * reads `shadowedChannels`, so a held channel holds her replies on it too.
 *
 * @param {string[]} heldChannels - from `SHADOWABLE_CHANNELS`, non-empty
 * @param {string} rungName - the stored rung's display name
 * @returns {string}
 */
function channelHoldFor(heldChannels, rungName) {
  const names = heldChannels.map((ch) => CHANNEL_NAMES[ch] || ch)
    .map((name) => name === 'Email' ? 'email' : name.replace(/^On-site/, 'on-site'));
  const list = names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  const held = `Messages on ${list} are${heldChannels.length === SHADOWABLE_CHANNELS.length ? ' all' : ''} held in Shadow for this store, ` +
    'both what Marva starts and her replies.';
  return heldChannels.length === SHADOWABLE_CHANNELS.length
    ? held
    : `${held} Messages she starts on other channels act as ${rungName}.`;
}

/**
 * What the WEB says about the ceiling, in the merchant's own voice, for the live flag. The server
 * computes it (governance-api's authority GET, `aboveCeiling`) because only the server knows
 * `AUTHORITY_ESCALATION_ENABLED`; the web renders what it is sent, so a flip changes the screen
 * and Ask together, with no web deploy.
 *
 * @param {boolean} escalationEnabled
 * @returns {string}
 */
function aboveCeilingForMerchant(escalationEnabled) {
  return escalationEnabled === true
    ? 'Above your ceiling she holds an email or SMS offer and asks you in the Inbox first; elsewhere she offers the ceiling.'
    : ABOVE_CEILING_COPY;
}

/** Inbox escalations are not Ask proposals, and the two are confirmed in different places. */
const INBOX_VS_PROPOSAL = 'Escalations are items Marva is holding in the Inbox; the merchant approves or rejects them there. ' +
  'They are not proposals. A proposal is only a change Ask prepared in this conversation, and it is confirmed with the button on Ask\'s own card.';

/**
 * What an item waiting on the merchant is, and what they can do about it — one per
 * `list_attention_items` source. `kind` is the stable token; `whatYouCanDo` is said to the model.
 */
const ATTENTION_ITEM_COPY = Object.freeze({
  escalation: Object.freeze({
    kind: 'escalation',
    whatYouCanDo: 'Approve or reject it in the Inbox. It is an escalation Marva is holding, not an Ask proposal: nothing said in this conversation approves it.'
  }),
  customer_question: Object.freeze({
    kind: 'customer_question',
    whatYouCanDo: 'Answer it. A customer asked something Marva could not answer herself; the merchant answers it in the Inbox, or confirms an answer Ask prepared when Ask can prepare changes.'
  }),
  shadow_decision: Object.freeze({
    kind: 'shadow_decision',
    whatYouCanDo: 'Already decided in Shadow, and nothing will be sent. Review it and give feedback if useful; there is nothing to approve or send.'
  })
});

/**
 * The model's statement for a rung; an unknown rung is reported as unknown rather than guessed.
 * @param {string} rung
 * @returns {string|null}
 */
function rungForModel(rung) {
  return Object.prototype.hasOwnProperty.call(RUNG_FOR_MODEL, rung) ? RUNG_FOR_MODEL[rung] : null;
}

module.exports = {
  RUNG_COPY,
  RUNG_MEANING_COPY,
  RUNG_FOR_MODEL,
  ABOVE_CEILING_COPY,
  INBOX_VS_PROPOSAL,
  ATTENTION_ITEM_COPY,
  aboveCeilingFor,
  aboveCeilingForMerchant,
  heldByModeFor,
  noSavedAuthorityFor,
  channelHoldFor,
  shadowStatementFor,
  SHADOWABLE_CHANNELS,
  CHANNEL_NAMES,
  rungForModel
};
